import React, { useState, useEffect } from 'react';
import Badge from '@atlaskit/badge';
import Button from '@atlaskit/button';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { colors, CustomThemeButton } from '@atlassian/jira-portfolio-3-common';
import type { OptionType } from '@atlassian/jira-portfolio-3-common/src/select/types';
import messages from './messages';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './styles.module.css';
import type { Props } from './types';

function PopupSelectButton({
	label,
	selectValues,
	getOptionValue,
	triggerProps,
	onClick,
	isSelected,
	isDisabled = false,
	isMulti = false,
	emptyValues = [],
	testId,
}: Props) {
	const intl = useIntl();
	const [buttonValue, setButtonValue] = useState('');
	const [badgeCount, setBadgeCount] = useState(0);

	useEffect(() => {
		let value = '';
		const getLabel = (option: OptionType) =>
			!emptyValues.includes(option.value) ? option.label : '';

		if (isMulti) {
			if (Array.isArray(selectValues) && selectValues.length > 0) {
				if (getOptionValue) {
					value = getOptionValue(selectValues[0]);
				} else {
					value = getLabel(selectValues[0]);
				}
				setBadgeCount(selectValues.length > 1 ? selectValues.length - 1 : 0);
			} else {
				setBadgeCount(0);
			}
		} else if (selectValues && !Array.isArray(selectValues)) {
			if (getOptionValue) {
				value = getOptionValue(selectValues);
			} else {
				value = getLabel(selectValues);
			}
		}

		setButtonValue(value);
	}, [isMulti, label, emptyValues, selectValues, getOptionValue]);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const disabledTheme = (current: any, themeProps: any) => ({
		...current(themeProps),
		buttonStyles: {
			...current(themeProps).buttonStyles,
			...{
				background: token('color.background.disabled', colors.N20A),
				color: token('color.text.disabled', colors.N70),
				cursor: 'not-allowed',
			},
		},
	});

	const content = (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
		<span className={styles.buttonLabel}>
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
			<span className={styles.buttonText}>
				{buttonValue !== '' ? (
					<>
						{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
						<strong>{label}:</strong> <span>{` ${buttonValue}`}</span>
					</>
				) : (
					label
				)}
			</span>
			{isMulti && badgeCount > 0 && (
				<span
					style={{
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						marginLeft: token('space.075', '6px'),
					}}
				>
					<Badge
						style={{
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
							backgroundColor: token('color.background.brand.bold', colors.N80),
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
							color: token('color.text.inverse', colors.N800),
						}}
					>{`+${badgeCount}`}</Badge>
				</span>
			)}
		</span>
	);
	if (isDisabled) {
		return (
			<CustomThemeButton theme={disabledTheme} testId={testId}>
				{content}
			</CustomThemeButton>
		);
	}

	return (
		<Button
			autoFocus={false}
			isSelected={isSelected || buttonValue !== ''}
			iconAfter={<ChevronDownIcon label="" />}
			onClick={() => {
				if (onClick) {
					onClick();
				}
			}}
			{...triggerProps}
			testId={testId}
			aria-label={intl.formatMessage(messages.filterButtonsArialabel, {
				buttonLabel: label,
				buttonSelectedValue: buttonValue !== '' ? buttonValue : null,
			})}
		>
			{content}
		</Button>
	);
}

export default PopupSelectButton;
